import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { DateTimePicker } from '@mui/x-date-pickers';
import { Controller, useForm } from 'react-hook-form';
import { CheckVerified01 } from 'untitledui-js-base';
import { RegistryIdNameLabel } from '../types/accident';
import { CarBranch, RentalTakeoverRequest } from '../types/rental';

interface RentFinishConfirmDialogProps {
  open: boolean;
  onClose?: () => void;
  onAccept?: (rentData: RentalTakeoverRequest) => void;
  onDeny?: () => void;
  isLoading?: boolean;
  carBranches?: CarBranch[];
  carStatuses?: RegistryIdNameLabel[];
}

export const RentFinishConfirmDialog = ({
  onDeny,
  onClose,
  onAccept,
  open,
  isLoading,
  carBranches,
  carStatuses
}: RentFinishConfirmDialogProps) => {
  const methods = useForm<RentalTakeoverRequest>({
    shouldUnregister: true,
    defaultValues: {
      takeover: {
        branchId: carBranches?.[0].id,
        note: '',
        carStatus: carStatuses?.[0].id
      },
      rental: {
        returnDate: new Date()
      }
    }
  });

  const { handleSubmit, control } = methods;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg">
      <DialogTitle id="alert-dialog-title">Dokončení zápůjčky</DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">Opravdu chcete dokončit tuto zápůjčku?</DialogContentText>
        <form onSubmit={handleSubmit((data) => onAccept?.(data))}>
          <Grid container spacing={2} pt={4} mb={3}>
            <Grid size={12}>
              <Controller
                control={control}
                name="rental.returnDate"
                render={({ field: { onChange, value } }) => (
                  <DateTimePicker label="Datum vrácení" value={value} onChange={onChange} />
                )}
              />
            </Grid>
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel
                  id="branchId-label"
                  sx={{
                    backgroundColor: 'white',
                    px: 0.5
                  }}
                >
                  Vráceno na pobočku
                </InputLabel>
                <Controller
                  control={control}
                  name="takeover.branchId"
                  render={({ field: { onChange, value } }) => (
                    <Select value={value} onChange={onChange} labelId="branchId-label">
                      {carBranches?.map((branch) => (
                        <MenuItem key={branch.id} value={branch.id}>
                          {branch.title}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
            </Grid>
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel
                  id="carStatus-label"
                  sx={{
                    backgroundColor: 'white',
                    px: 0.5
                  }}
                >
                  Stav vozu
                </InputLabel>
                <Controller
                  control={control}
                  name="takeover.carStatus"
                  render={({ field: { onChange, value } }) => (
                    <Select value={value} onChange={onChange} labelId="carStatus-label">
                      {carStatuses?.map((status) => (
                        <MenuItem key={status.id} value={status.id}>
                          {status.label}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
            </Grid>
            <Grid size={12}>
              <Controller
                control={control}
                name="takeover.note"
                render={({ field: { onChange, value } }) => (
                  <TextField
                    placeholder="Poznámka k převzetí"
                    variant="outlined"
                    value={value}
                    onChange={onChange}
                    fullWidth
                    multiline
                    rows={3}
                  />
                )}
              />
            </Grid>
          </Grid>
          <Stack width={1} justifyContent="center">
            <Button
              variant="contained"
              color="success"
              type="submit"
              loading={isLoading}
              startIcon={<CheckVerified01 />}
              size="large"
            >
              Dokončit zápůjčku
            </Button>
          </Stack>
        </form>
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: 'center',
          flexDirection: { xs: 'column-reverse', md: 'row' },
          gap: 2
        }}
      >
        <Button onClick={onDeny}>Zavřít</Button>
      </DialogActions>
    </Dialog>
  );
};
